"use client";

// useBandHeight — publish the navigator band's bottom edge, in VIEWPORT pixels,
// as `--os-band-h` on :root.
//
// Why this exists (law_one_ground_gradient): the frosted band over the navigator
// has to be tinted with THE ground gradient, sampled at the point on screen it
// actually covers — otherwise it mints a second, slightly-off gradient of its
// own and reads as a coloured slab sitting on the ground. The only way to sample
// the ground correctly is to paint the gradient on a layer that IS the viewport
// and then mask it back to the band's shape. `background-attachment: fixed` looks
// like a shortcut for that and is not one: it is unreliable in exactly this
// position (and inert on iOS), which is what left the band a different colour
// from the ground beneath it.
//
// So the band's shape becomes mask geometry, and mask geometry needs one number:
// where the band ends on screen. That's this.

import { useEffect, type RefObject } from "react";

const VAR = "--os-band-h";
/** the tail travels with the height: a post publishes a longer one than an
 *  index, and the band's tint mask reads it (`--os-band-tail`, globals.css). */
const TAIL_VAR = "--os-band-tail";
/** the band fades out a little past the navigator's own bottom edge. */
const TAIL = 17.6; // 1.1rem

export function useBandHeight(
  ref: RefObject<HTMLElement | null>,
  active = true,
  varName: string = VAR,
  tail: number = TAIL,
): void {
  useEffect(() => {
    const root = document.documentElement;
    if (!active) {
      root.style.setProperty(varName, "0px");
      return () => root.style.removeProperty(varName);
    }
    // the band is the head's own pseudo-element (globals.css, THE BAND) — nothing
    // to arm. Only the shared geometry publishes its tail; a surface with a
    // variable of its own (the artifact reader's `--art-band-h`) has its own veil.
    const shared = varName === VAR;
    if (shared) root.style.setProperty(TAIL_VAR, `${tail}px`);
    let frame = 0;
    let last = -1;
    const measure = () => {
      frame = 0;
      const el = ref.current;
      if (!el) return;
      const h = Math.round(Math.max(0, el.getBoundingClientRect().bottom + tail));
      // a write on :root restyles the document: once the head is pinned the
      // value stops changing, so the scroll frames after that write nothing
      if (h === last) return;
      last = h;
      root.style.setProperty(varName, `${h}px`);
    };
    // the band moves with every scroll (the navigator is sticky, so it only
    // settles once it locks) — coalesce to one measure per frame.
    const schedule = () => { if (!frame) frame = requestAnimationFrame(measure); };
    measure();
    // CAPTURE phase: scroll events don't bubble, but they do capture — so this
    // one listener catches the window AND any inner scroller (the artifact
    // reader scrolls its own column, not the page).
    window.addEventListener("scroll", schedule, { passive: true, capture: true });
    window.addEventListener("resize", schedule);
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(schedule) : null;
    if (ro && ref.current) ro.observe(ref.current);
    return () => {
      if (frame) cancelAnimationFrame(frame);
      window.removeEventListener("scroll", schedule, true);
      window.removeEventListener("resize", schedule);
      ro?.disconnect();
      root.style.removeProperty(varName);
      if (shared) root.style.removeProperty(TAIL_VAR);
    };
  }, [ref, active, varName, tail]);
}
